import { useState } from 'react';
import { Download, Mail } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { createLead } from '../../lib/api';

import { img } from '../../lib/img';

export default function LeadMagnet() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email');
      return;
    }
    setSubmitting(true);
    try {
      await createLead({ email, source: 'lead-magnet-home' });
      setSent(true);
      toast.success('Your free guide is on the way.');
      setEmail('');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail[0]?.msg : (detail || 'Could not submit. Please try again.');
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="py-24 lg:py-36 bg-ivory-warm">
      <div className="max-w-[1440px] mx-auto px-5 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7 reveal">
          <p className="text-[11px] tracking-[0.3em] uppercase text-champagne-deep mb-6">Free Guide — Lead Magnet</p>
          <h2 className="font-serif text-4xl lg:text-6xl leading-[1.05] text-charcoal">
            Build a wardrobe that <em className="italic-accent">works</em> before you even think about trends.
          </h2>
          <p className="mt-6 text-charcoal/70 leading-relaxed max-w-xl">
            Download Mondaine’s free guide to the 10 essential pieces every modern Indian man needs — for work, weekends, dates, travel and occasions.
          </p>

          <form onSubmit={submit} className="mt-10 flex flex-col sm:flex-row gap-3 max-w-lg">
            <div className="relative flex-1">
              <Mail size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-charcoal/50" />
              <Input
                type="email"
                value={email}
                onChange={(e)=>setEmail(e.target.value)}
                placeholder="your@email.com"
                className="h-14 pl-11 rounded-none border-charcoal/20 bg-ivory focus:ring-champagne text-charcoal"
                required
              />
            </div>
            <Button data-testid="lead-magnet-submit-btn" type="submit" disabled={submitting} className="h-14 bg-charcoal hover:bg-charcoal-soft text-ivory rounded-none px-7 text-[11px] tracking-[0.22em] uppercase disabled:opacity-60">
              {submitting ? 'Sending…' : 'Send Me The Free Guide'}
            </Button>
          </form>
          {sent && <p className="mt-4 text-sm text-champagne-deep">Sent. Check your inbox in a few minutes.</p>}
          <p className="mt-4 text-xs text-charcoal/50">No spam. Unsubscribe with a single tap.</p>
        </div>

        <div className="lg:col-span-5 reveal">
          <div className="relative aspect-[3/4] max-w-sm mx-auto bg-ivory border border-ivory-deep p-8 shadow-[0_30px_60px_-30px_rgba(0,0,0,0.25)]">
            <div className="flex items-center gap-2 mb-6">
              <span className="h-px w-8 bg-champagne-deep" />
              <span className="text-[10px] tracking-[0.3em] uppercase text-champagne-deep">Mondaine Style</span>
            </div>
            <p className="font-display text-xs tracking-[0.35em] text-charcoal/60">A FREE GUIDE</p>
            <h3 className="font-serif text-3xl mt-3 leading-tight text-charcoal">
              The 10-Piece <em className="italic-accent">Wardrobe</em> Every Modern Indian Man Needs
            </h3>
            <ul className="mt-8 space-y-2.5">
              {['The white shirt, reconsidered','A pair of trousers that travels','The off-duty knit','One blazer, three roles','Denim that earns respect'].map((t,i)=>(
                <li key={i} className="flex gap-3 text-sm text-charcoal/70">
                  <span className="font-display text-champagne-deep">{String(i+1).padStart(2,'0')}</span>
                  <span>{t}</span>
                </li>
              ))}
              <li className="text-xs text-charcoal/40 pt-2">+ five more, considered in detail</li>
            </ul>
            <div className="absolute bottom-6 right-6 flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-champagne-deep">
              <Download size={12} /> PDF · 24 PAGES
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
